import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, type ApiClient } from "../../../api/api-client";
import { inventorySchema, type InventoryResponse } from "../../../api/schemas";
import { appendInventoryItems, assertInventoryPageIdentity, inventoryDisplay, mergeInventoryPage, retainInventoryPage, sameInventoryIdentity } from "./inventory-pagination";

type State = {
  context: string; data: InventoryResponse | null; loading: boolean; loadingMore: boolean;
  head: InventoryResponse | null; tail: NonNullable<InventoryResponse["items"]>;
  continuation: InventoryResponse | null; hasLoadedMore: boolean;
  error: string | null; newSnapshot: boolean; revision: number;
};
const empty = (context: string): State => ({ context, data: null, loading: false, loadingMore: false,
  head: null, tail: [], continuation: null, hasLoadedMore: false,
  error: null, newSnapshot: false, revision: 0 });

export function useInventoryBrowser(api: ApiClient, context: string, enabled: boolean, token: string | null,
  owner: string | null, interval: number, refreshRevision: number, readContext: () => string, presentationActive = true,
  expectedChain?: number | null, expectedCollection?: string | null) {
  const [stored, setState] = useState(() => empty(context));
  const state = stored.context === context ? stored : empty(context);
  const current = useRef(state);
  current.current = state;
  const root = useRef<AbortController | null>(null);
  const page = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const path = `/nfts?limit=20${token ? `&sourceToken=${encodeURIComponent(token)}` : ""}`;

  const loadRoot = useCallback(async (replace = false) => {
    if (!enabled || readContext() !== context || (!replace && root.current)) return;
    if (replace) {
      generation.current++;
      root.current?.abort(); page.current?.abort(); page.current = null;
    }
    const revision = generation.current;
    const abort = new AbortController(); root.current = abort;
    const valid = () => !abort.signal.aborted && readContext() === context && revision === generation.current;
    setState((s) => ({ ...(s.context === context ? s : empty(context)), loading: true,
      ...(replace ? { loadingMore: false } : {}) }));
    try {
      const data = retainInventoryPage(await api.get(path, inventorySchema, abort.signal));
      if (!valid()) return;
      assertInventoryPageIdentity(data, { owner, chainId: expectedChain, collection: expectedCollection, sourceToken: token });
      const pagePending = page.current !== null;
      setState((s) => {
        if (!valid() || s.context !== context) return s;
        if (!data.inventoryAvailable || data.items === null) return { ...s, loading: false,
          data: s.data?.inventoryAvailable ? s.data : data,
          error: data.blockingReasons.join(", ") || "Inventory is unavailable" };
        if (s.head && !sameInventoryIdentity(s.head, data)) return { ...s, loading: false,
          error: "Inventory identity does not match the current listing" };
        // Polling updates only the head. A cursor already used for browsing (or
        // in flight) still belongs to its original snapshot, even after this read.
        const restart = replace || (!s.hasLoadedMore && !pagePending && !s.newSnapshot);
        const continuation = restart || !s.continuation ? data : s.continuation;
        return { ...s, head: data, continuation, data: inventoryDisplay(data, s.tail, continuation),
          loading: false, error: null, newSnapshot: replace ? false : s.newSnapshot,
          revision: s.revision + 1 };
      });
    } catch (error) {
      if (valid()) setState((s) => s.context !== context ? s : { ...s, loading: false,
        error: error instanceof ApiError ? error.code : error instanceof Error ? error.message : "Inventory refresh failed" });
    } finally { if (root.current === abort) root.current = null; }
  }, [api, context, enabled, owner, path, readContext, expectedChain, expectedCollection, token]);

  useEffect(() => {
    setState(empty(context));
    // This is a request-generation counter, not a captured DOM ref. Invalidate
    // the latest generation, including work started after this effect mounted.
    return () => { generation.current += 1; root.current?.abort(); page.current?.abort(); root.current = null; page.current = null; };
  }, [context]);
  useEffect(() => {
    const poll = () => { loadRoot().catch(() => console.error("Inventory polling failed unexpectedly.")); };
    poll();
  }, [loadRoot, refreshRevision]);
  useEffect(() => {
    if (!presentationActive) return;
    const poll = () => { loadRoot().catch(() => console.error("Inventory polling failed unexpectedly.")); };
    const backgroundPoll = () => { if (document.visibilityState !== "hidden") poll(); };
    // Returning to presentation reads the head without resetting retained pages.
    // The initial/explicit read above is single-flight with this request.
    backgroundPoll();
    const timer = setInterval(backgroundPoll, interval);
    document.addEventListener("visibilitychange", backgroundPoll);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", backgroundPoll); };
  }, [loadRoot, interval, presentationActive]);

  const loadMore = useCallback(async () => {
    const before = current.current;
    const cursor = before.continuation?.nextCursor;
    if (!enabled || readContext() !== context || before.context !== context || !cursor || page.current || root.current ||
      before.newSnapshot || before.loading || !owner) return;
    const abort = new AbortController(); page.current = abort;
    const revision = generation.current;
    const valid = () => !abort.signal.aborted && readContext() === context && revision === generation.current;
    setState((s) => ({ ...s, loadingMore: true }));
    try {
      const data = retainInventoryPage(await api.get(`${path}&cursor=${encodeURIComponent(cursor)}`, inventorySchema, abort.signal));
      if (!valid()) return;
      assertInventoryPageIdentity(data, { owner, chainId: expectedChain, collection: expectedCollection, sourceToken: token });
      setState((s) => {
        if (!valid() || s.context !== context || s.continuation?.nextCursor !== cursor) return s;
        const merged = mergeInventoryPage(s.continuation, data, owner);
        if (merged === s.continuation || !merged || !s.head) return { ...s, loadingMore: false, newSnapshot: true,
          error: "The next page could not be verified against its cursor snapshot. Update the inventory to continue." };
        const tail = appendInventoryItems(s.tail, data.items ?? []);
        return { ...s, tail, continuation: data, hasLoadedMore: true,
          data: inventoryDisplay(s.head, tail, data), loadingMore: false, error: null,
          revision: s.revision + 1 };
      });
    } catch (error) {
      if (valid()) setState((s) => s.context !== context ? s : { ...s, loadingMore: false,
        newSnapshot: s.newSnapshot || error instanceof ApiError && error.code === "NFT_INVENTORY_SNAPSHOT_CHANGED",
        error: error instanceof ApiError ? error.code : error instanceof Error ? error.message : "Loading more NFTs failed" });
    } finally { if (page.current === abort) page.current = null; }
  }, [api, context, enabled, owner, path, readContext, expectedChain, expectedCollection, token]);
  return { ...state, loadMore, refresh: () => loadRoot(true) };
}
